import React, { useEffect, useRef, useState } from "react";
import { useDeviceType } from "../../hooks/useDeviceType";
import { useHideMenu } from "../../hooks/useHideMenu";
import { useScrollMenuHandler } from "../../hooks/useScrollMenuHandler";
import menuStore from '../../store/MenuStore';
import FooterSection from "../base/FooterSection";
import FooterSectionMobile from "../base/FooterSectionMobile";
import IntroduceSection3 from "./IntroduceSection3";
import VisionSection1 from "./VisionSection1";
import VisionSection2 from "./VisionSection2";
import VisionSection3 from "./VisionSection3";
import VisionSection4 from "./VisionSection4";
import VisionSection5 from "./VisionSection5";

const IntroduceVisionPage: React.FC = () => {
  const { isMobile } = useDeviceType();

  const sectionRef = useRef<HTMLDivElement>(null);
  const sectionRef2 = useRef<HTMLDivElement>(null);
  const sectionRef4 = useRef<HTMLDivElement>(null);
  const sectionRef5 = useRef<HTMLDivElement>(null);
  const sectionRef6 = useRef<HTMLDivElement>(null);

  const [showAction1, setShowAction1] = useState(false);
  const [showAction2, setShowAction2] = useState(false);
  const [showAction3, setShowAction3] = useState(false);

  const fullpageRef = useRef<HTMLDivElement>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const prevScrollYRef = useRef(0);
  const windowHeight = window.innerHeight;

  useScrollMenuHandler();
  useHideMenu();

  useEffect(() => {
    menuStore.hide();
    menuStore.setBackgroundColor('transparent');
    menuStore.setDarkMode(true);
    const timer = setTimeout(() => {
      menuStore.attach();
      menuStore.show();
    }, 2300);

    return () => clearTimeout(timer); // cleanup
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // 스크롤 방향 감지
      const currentScrollY = window.scrollY;
      const deltaY = currentScrollY - prevScrollYRef.current;
      const scrollDown = deltaY > 0;
      prevScrollYRef.current = currentScrollY;

      // 각 섹션의 높이 계산
      const section1Height = sectionRef.current?.getBoundingClientRect().height ?? windowHeight;
      const section2Height = sectionRef2.current?.getBoundingClientRect().height ?? windowHeight;
      const section3StickyHeight = window.innerHeight * 5; // sticky 섹션 높이

      // 각 섹션의 스크롤 위치 계산
      const section1ScrollY = currentScrollY;
      const section2ScrollY = currentScrollY - section1Height;
      const section3ScrollY = currentScrollY - section1Height - section2Height;

      // pageIndex 계산: section3ScrollY를 기반으로 계산
      if (section3ScrollY >= 0 && section3ScrollY < section3StickyHeight) {
        const progress = section3ScrollY / section3StickyHeight; // 0~1
        const idx = Math.min(4, Math.max(0, Math.floor(progress * 5))); // 0~4
        setPageIndex(idx);
      } else if (section3ScrollY < 0) {
        // 섹션3이 아직 시작되지 않았으면 첫 페이지
        setPageIndex(0);
      } else if (section3ScrollY >= section3StickyHeight) {
        // 섹션3이 끝났으면 마지막 페이지
        setPageIndex(4);
      }

      if (!fullpageRef.current) return;

      const rect = fullpageRef.current.getBoundingClientRect();
      if (rect.top <= 0 && rect.top >= -(window.innerHeight * 5)) {
        menuStore.hide();
      }

      // showAction1: Section2 애니메이션
      if (scrollDown) {
        if (section1ScrollY > 150 && !showAction1) {
          setShowAction1(true);
        }
      }

      // showAction2: Section4 애니메이션
      if (scrollDown) {
        const section4Rect = sectionRef4.current?.getBoundingClientRect();
        if (section4Rect) {
          if (section4Rect.top < windowHeight && section4Rect.bottom > 0) {
            setShowAction2(true);
          }
        }
      }

      // showAction3: Section5 애니메이션
      if (scrollDown) {
        const section5Rect = sectionRef5.current?.getBoundingClientRect();
        if (section5Rect) {
          if (section5Rect.top < windowHeight && section5Rect.bottom > 0) {
            setShowAction3(true);
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  const totalPages = 5;
  const stickyHeight = window.innerHeight * totalPages;

  return (
    <div style={{ position: "relative", backgroundColor: 'black', boxSizing: 'border-box', margin: '0px', padding: '0px' }}>
      {/* Section 1 */}
      <div ref={sectionRef} style={{ width: '100%', overflowX: 'hidden' }}>
        <VisionSection1 />
      </div>

      <div ref={sectionRef2} style={{ width: '100%', overflowX: 'hidden' }}>
        <VisionSection2 isInView={showAction1} />
      </div>

      {/* Section 3 (뷰포트에 고정) */}
      <div ref={fullpageRef} style={{ position: "relative", height: `${stickyHeight}px` }}>
        <section
          style={{
            position: "sticky",
            top: 0,
            height: `100vh`,
            background: "black",
            zIndex: 2,
            width: '100%'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100vh',
              overflowX: 'hidden',
              opacity: 1
            }}
          >
            {isMobile ? (
              <IntroduceSection3 pageIndex={pageIndex} progress={0.000001} />
            ) : (
              <VisionSection3 pageIndex={pageIndex} />
            )}
          </div>
        </section>
      </div>


      <div ref={sectionRef4} style={{ width: '100%', overflowX: 'hidden', position: "relative", zIndex: 5 }}>
        <VisionSection4 isInView={showAction2} />
      </div>

      <div ref={sectionRef5} style={{ width: '100%', overflowX: 'hidden', position: "relative", zIndex: 5 }}>
        <VisionSection5 isInView={showAction3} />
      </div>

      <div ref={sectionRef6} style={{ width: '100%', overflowX: 'hidden', position: "relative", zIndex: 5 }}>
        {isMobile ? <FooterSectionMobile /> : <FooterSection />}
      </div>
    </div>
  );
};

export default IntroduceVisionPage;

